/**
 * Ask Olivia's web side (H3b-6): the answers' shapes, the guide's citations in the reader's words, where a guide screen or
 * a diagram's box opens from the current page, the panel's remembered size, and "Where do I…" generated from the
 * navigation (nav/nav-config.ts), so a new screen in the rail is a new line here without anyone writing it twice.
 *
 * Nothing here reads a client's records: the panel reads /api/help/* only, and a link is the current address with the
 * client's code in it, never anything fetched.
 *
 * PROVENANCE: C:\Valentin\shared\guide\parts.ts (citedPart), shared\guide\route-step.ts (diagramRouteHref,
 * diagramRouteNeeds) and src\components\help\AskHelper.tsx (readPanelSize, citationLine) @ d32c53d (main, H2/H3a),
 * adapted 2026-10-11. CHANGES: Olivia's routes (/c/:client/… with :assessment, :practice and a record's code) and its
 * navigation as the source of "Where do I…"; the panel's key is olivia.help.panelSize.
 */

import type { Translate } from '../i18n/core'
import { CLIENT_EXPERT_NAV, CLIENT_WORK_NAV, hrefFor, isBuilt, PORTFOLIO_NAV, visibleTo, type NavItem, type NavSection } from '../nav/nav-config'
import type { GuideDiagrams } from './guide/diagrams'

// ── the guide as the server serves it ───────────────────────────────────────────────────────────────────────────────

export interface GuideStep {
  id: string
  title: string
  you_do: string
  it_does: string
  input_screen: string
  result_screen: string
  you_decide: string
}

export interface GuideContent {
  purpose: string
  steps: GuideStep[]
  glossary: { term: string; meaning: string }[]
  faq: { q: string; a: string }[]
  ai: { does: string[]; never: string[] }
  screens: { id: string; name: string; path?: string; what: string }[]
}

export interface HelpGuide {
  guide: {
    id: string
    version: string
    status: string
    product: string
    avatar: string
    sha256: string
    topics: { version: string; status: string; sha256: string }
    superseded: string[]
    content: { en: GuideContent; sv?: GuideContent }
  }
  provider: { model: string; engine: 'openrouter' | 'anthropic' } | null
  limits: { asksPerHour: number; aiPerHour: number; questionChars: number }
}

export interface HelpDiagramsAnswer {
  diagrams: GuideDiagrams
  sha256: string
  rules: Record<string, { en: string; sv: string }>
}

export type HelpReason = 'not_configured' | 'rate_limited' | 'unavailable' | 'spend_cap' | 'uncited' | 'figure' | 'overstated' | 'personal_data' | 'credentials'

export interface HelpAnswer {
  mode: 'ai' | 'keywords' | 'none' | 'refused'
  reason: HelpReason | null
  paragraphs: string[]
  cited: string[]
  seeAlso: string[]
  screens: string[]
  model: string | null
}

/** The model's name as a person reads it. */
export function modelName(model: string): string {
  const bare = model.slice(model.indexOf(':') + 1)
  if (bare === 'z-ai/glm-5.3') return 'GLM 5.3'
  if (bare === 'z-ai/glm-5.3-flash') return 'GLM 5.3 Flash'
  if (bare === 'mistralai/mistral-large-4-0') return 'Mistral Large 4'
  if (bare === 'moonshotai/kimi-k2.6') return 'Kimi K2.6'
  if (bare === 'claude-sonnet-5') return 'Claude Sonnet 5'
  if (bare === 'claude-opus-5-5') return 'Claude Opus 5.5'
  return bare
}

// ── citations ───────────────────────────────────────────────────────────────────────────────────────────────────────

const PART_ID = /^(?:(step|screen):([a-z0-9]+(?:-[a-z0-9]+)*)|(glossary|faq):([1-9][0-9]?)|(ai)|(brief))$/

/** "step 5, Review by a second person" and the others, in the reader's language, from the guide's own titles. */
export function citationLabel(t: Translate, content: GuideContent, id: string): string | null {
  const m = PART_ID.exec(id)
  if (!m) return null
  if (m[1] === 'step') {
    const i = content.steps.findIndex((s) => s.id === m[2])
    return i < 0 ? null : t('help.cite.step', { n: i + 1, title: content.steps[i]?.title ?? '' })
  }
  if (m[1] === 'screen') {
    const s = content.screens.find((x) => x.id === m[2])
    return s ? t('help.cite.screen', { title: s.name }) : null
  }
  if (m[3] === 'glossary') {
    const g = content.glossary[Number(m[4]) - 1]
    return g ? t('help.cite.glossary', { title: g.term }) : null
  }
  if (m[3] === 'faq') {
    const f = content.faq[Number(m[4]) - 1]
    return f ? t('help.cite.faq', { title: f.q }) : null
  }
  return m[5] ? t('help.cite.ai') : t('help.cite.brief')
}

/** "From: a; b", or null when nothing is cited. */
export function citationLine(t: Translate, content: GuideContent, ids: readonly string[]): string | null {
  const labels = ids.map((id) => citationLabel(t, content, id)).filter((x): x is string => x !== null)
  return labels.length === 0 ? null : `${t('help.from')} ${labels.join('; ')}`
}

// ── where a route opens from here ───────────────────────────────────────────────────────────────────────────────────

/** What a route pattern names, besides the client: the analysis, a practice, or one record. */
export type RouteNeed = 'client' | 'assessment' | 'practice' | 'record'

function segments(pathname: string): string[] {
  return (pathname.split(/[?#]/, 1)[0] ?? '').split('/').filter(Boolean).map((s) => {
    try {
      return decodeURIComponent(s)
    } catch {
      return s
    }
  })
}

/** The values the current address names: its client, its analysis, its practice and the record it shows. */
export function routeParams(pathname: string): Record<string, string> {
  const s = segments(pathname)
  const out: Record<string, string> = {}
  if (s[0] !== 'c' || !s[1]) return out
  out.client = s[1]
  if (s[2] === 'assessments' && s[3]) {
    out.assessment = s[3]
    if ((s[4] === 'practices' || s[4] === 'gaps') && s[5]) out.practice = s[5]
  }
  if (s[2] === 'inquiries' && s[3]) out.inquiry = s[3]
  if (s[2] === 'requests' && s[3]) out.request = s[3]
  if (s[2] === 'handoffs' && s[3]) out.handoff = s[3]
  if (s[2] === 'registers' && s[3] === 'actions' && s[4]) out.action = s[4]
  return out
}

/** A route pattern with the current address's values put in, or null when one of them is not on the current page. */
export function routeHref(route: string, pathname: string): string | null {
  const params = routeParams(pathname)
  let missing = false
  const href = route.replace(/:([a-z]+)/g, (_m, name: string) => {
    const v = params[name]
    if (v === undefined) {
      missing = true
      return `:${name}`
    }
    return encodeURIComponent(v)
  })
  return missing ? null : href
}

/** What to open first, when routeHref gives no link: the first value the route needs that the page does not name. */
export function routeNeeds(route: string, pathname: string): RouteNeed | null {
  const params = routeParams(pathname)
  for (const [, name] of route.matchAll(/:([a-z]+)/g)) {
    if (name && params[name] === undefined) return name === 'client' || name === 'assessment' || name === 'practice' ? name : 'record'
  }
  return null
}

/** The current address without its query or a trailing slash, to mark "You are here". */
export function currentPath(pathname: string): string {
  const s = segments(pathname)
  return `/${s.map((x) => encodeURIComponent(x)).join('/')}`
}

/** The box of the current page in a diagram's walk, or null. */
export function currentBox(nodes: readonly { id: string; routes?: string[] }[], steps: readonly string[], pathname: string): string | null {
  const here = currentPath(pathname)
  const byId = new Map(nodes.map((n) => [n.id, n]))
  return steps.find((id) => (byId.get(id)?.routes ?? []).some((r) => routeHref(r, pathname) === here)) ?? null
}

/**
 * The diagram that opens first on the current page: the one that fits the area when it has a box here, else any with a box
 * here, else the one that fits the area, else the first.
 */
export function defaultDiagram(diagrams: GuideDiagrams['diagrams'], pathname: string): string | null {
  const s = segments(pathname)
  const want =
    s[0] !== 'c' ? 'onboarding'
    : s[2] === 'progress' || s[2] === 'registers' || s[2] === 'requests' ? 'proof-loop'
    : s[2] === 'handoffs' || (s[2] === 'assessments' && s[4] === 'gaps') ? 'family'
    : s[2] === 'ai' || s[2] === 'modules' || s[2] === 'members' ? 'ai-boundary'
    : s[2] === 'assessments' ? 'maturity'
    : 'onboarding'
  const hasHere = (d: GuideDiagrams['diagrams'][number]) => currentBox(d.nodes, d.steps, pathname) !== null
  const fits = diagrams.find((d) => d.id === want)
  if (fits && hasHere(fits)) return fits.id
  return diagrams.find(hasHere)?.id ?? fits?.id ?? diagrams[0]?.id ?? null
}

// ── the panel's size ────────────────────────────────────────────────────────────────────────────────────────────────

export const PANEL_SIZE_KEY = 'olivia.help.panelSize'
export type PanelSize = 'large' | 'compact'

/** Large unless this browser remembers compact; storage that throws (blocked, private) reads as large. */
export function readPanelSize(): PanelSize {
  try {
    return window.localStorage.getItem(PANEL_SIZE_KEY) === 'compact' ? 'compact' : 'large'
  } catch {
    return 'large'
  }
}

export function writePanelSize(size: PanelSize): void {
  try {
    window.localStorage.setItem(PANEL_SIZE_KEY, size)
  } catch {
    // not remembered: this visit only
  }
}

// ── "Where do I…", from the navigation ──────────────────────────────────────────────────────────────────────────────

export interface WhereItem {
  id: string
  item: NavItem
  /** The address to open, or null when it needs a client and the page names none. */
  href: string | null
}

export interface WhereGroup {
  id: 'portfolio' | 'client-work' | 'client-expert'
  sections: { section: NavSection; items: WhereItem[] }[]
}

/**
 * Every built screen of the navigation the person may see, grouped as the rail groups them: across your clients, a client
 * in Work, a client in Expert. A client's screen links into the current client, or says to open one first.
 */
export function whereGroups(firmRoles: readonly string[], client: string | null): WhereGroup[] {
  const group = (id: WhereGroup['id'], sections: readonly NavSection[]): WhereGroup => ({
    id,
    sections: sections
      .map((section) => ({
        section,
        items: section.items
          .filter((i) => isBuilt(i) && visibleTo(i, firmRoles))
          .map((item) => ({ id: item.id, item, href: item.to.includes(':client') ? (client ? hrefFor(item, client) : null) : item.to })),
      }))
      .filter((s) => s.items.length > 0),
  })
  return [group('portfolio', PORTFOLIO_NAV), group('client-work', CLIENT_WORK_NAV), group('client-expert', CLIENT_EXPERT_NAV)]
}
